import type { CooldownRecord, EncounterState, TimedEffectRecord } from './encounter.ts';
import {
  applyEncounterPhaseDefault,
  applyEvadeResultDefault,
  applyIncomingAttacksDefault,
  migrateEncounterThroughV4,
  transferEncounterOwnedData,
  validateEncounter,
} from './encounter.ts';
import { cooldownDurationSeconds } from '../rules/gameTime.ts';

/**
 * A crawler's RUNTIME state: everything that is currently true of them but is
 * not part of the permanent character sheet.
 *
 * This exists because most of Table 8 outlives a fight. Fatigued lasts until a
 * long rest; a Long-Term Major Injury until a full day's rest; Powerful Strike's
 * cooldown is 30 hours. Keeping those inside `EncounterState` meant they
 * vanished the moment combat ended.
 *
 * The split:
 *  - `active` and `cooldowns` belong to the CRAWLER and persist across restarts,
 *    whether or not a fight is in progress;
 *  - `encounter` is present only during a fight and holds the round number and
 *    Actions remaining, which have no meaning outside one.
 *
 * Nothing here is permanent character data: it is all removable, expires, and
 * is never written into the saved sheet.
 */
export const RUNTIME_SCHEMA_VERSION = 2;

export interface RuntimeState {
  schemaVersion: number;
  characterId: string;
  updatedAt: string;
  /** Active Buffs and Debuffs, in or out of combat. */
  active: TimedEffectRecord[];
  /** Cooldowns. Round- and scene-scoped ones end with the encounter; longer ones do not. */
  cooldowns: CooldownRecord[];
  /**
   * Total IN-GAME seconds that have passed for this crawler.
   *
   * A READOUT, not the clock. Expiry is driven by the time remaining on each
   * Effect and cooldown, so nothing depends on this number being right; it
   * exists so the Play Sheet can say how long the crawler has been at it, and
   * so a test can assert that sixty rounds took ten minutes.
   */
  elapsedSeconds: number;
  /** Null when the crawler is not in a fight. */
  encounter: EncounterState | null;
}

export function createRuntime(characterId: string, updatedAt: string): RuntimeState {
  return {
    schemaVersion: RUNTIME_SCHEMA_VERSION,
    characterId,
    updatedAt,
    active: [],
    cooldowns: [],
    elapsedSeconds: 0,
    encounter: null,
  };
}

export interface RuntimeValidation {
  valid: boolean;
  errors: string[];
}

export function validateRuntime(value: unknown): RuntimeValidation {
  const errors: string[] = [];
  if (typeof value !== 'object' || value === null) {
    return { valid: false, errors: ['Runtime record is not an object.'] };
  }
  const r = value as Record<string, unknown>;
  if (typeof r['characterId'] !== 'string' || r['characterId'].length === 0) {
    errors.push('Runtime state is missing a characterId.');
  }
  if (typeof r['schemaVersion'] !== 'number') {
    errors.push('Runtime state is missing a schemaVersion.');
  } else if (r['schemaVersion'] > RUNTIME_SCHEMA_VERSION) {
    errors.push('Runtime state was saved by a newer version of the app.');
  }
  if (!Array.isArray(r['active'])) errors.push('active must be a list.');
  if (!Array.isArray(r['cooldowns'])) errors.push('cooldowns must be a list.');
  if (typeof r['elapsedSeconds'] !== 'number' || r['elapsedSeconds'] < 0) {
    errors.push('elapsedSeconds must be a number of seconds.');
  }

  // THE NESTED ENCOUNTER IS PART OF THIS RECORD'S CONTRACT.
  //
  // The type is `EncounterState | null`, and the two states are NOT the same.
  // `null` is a crawler who is not fighting, which is most of their life. A
  // MISSING field is a record that never said, and treating the two alike lets
  // a truncated or hand-edited file present itself as a crawler at rest.
  //
  // This is validation, so it does not manufacture the answer. Nothing here
  // supplies a `null` - if a genuinely older runtime schema ever needs one, that
  // belongs in migration, before this runs. Runtime version 1 already carried
  // the field: the 1 -> 2 step adds `elapsedSeconds` and cooldown seconds and
  // nothing else, so no canonicalization is owed.
  if (!('encounter' in r)) {
    errors.push('Runtime state is missing an encounter field. Use null when not in a fight.');
  } else if (r['encounter'] !== null) {
    const encounter = r['encounter'];
    if (typeof encounter !== 'object' || encounter === undefined || Array.isArray(encounter)) {
      errors.push('encounter must be an encounter object or null.');
    } else {
      const nested = validateEncounter(encounter);
      for (const error of nested.errors) errors.push(`encounter: ${error}`);
      // Identity has to agree. A runtime record is only evidence about the
      // crawler it names, and an encounter naming someone else inside it is not
      // a fight this crawler is in.
      const nestedId = (encounter as Record<string, unknown>)['characterId'];
      if (typeof nestedId === 'string' && nestedId !== r['characterId']) {
        errors.push(
          `encounter belongs to character "${nestedId}", not "${String(r['characterId'])}".`,
        );
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * 1 -> 2: in-game elapsed time.
 *
 * Version 1 had no clock at all, so a cooldown measured in hours had no record
 * of how much of it had run. There is nothing to recover: the elapsed total
 * starts at zero, and a time-based cooldown is given its FULL duration again.
 *
 * Restarting those cooldowns is deliberate. The alternative - assuming they had
 * already finished - would silently hand back a 30-hour ability the crawler may
 * have used minutes ago. Waiting slightly too long is recoverable at the table;
 * an ability that quietly recharges itself is not.
 *
 * ---
 *
 * THIS FUNCTION IS ALSO THE ONLY AUTHORITY FOR THE NESTED ENCOUNTER.
 *
 * `EncounterState` lives inside this record and has its own schema version and
 * its own migration chain, but nothing was calling it: `migrateEncounterRecord`
 * and `validateEncounter` were unreachable from the loading path, so a saved
 * encounter was never migrated and never validated. That was harmless only
 * because the encounter shape had not changed since it moved in here. It stops
 * being harmless the moment a field is added to `EncounterState`.
 *
 * The nested step runs whenever a nested encounter EXISTS, not inside one outer
 * version branch. A current runtime record can carry an encounter of any
 * version the encounter chain knows about, independently of its own version.
 */
export function migrateRuntimeRecord(value: unknown): unknown {
  if (typeof value !== 'object' || value === null) return value;
  let record = { ...(value as Record<string, unknown>) };

  if (record['schemaVersion'] === 1) {
    record['elapsedSeconds'] = 0;
    const cooldowns = Array.isArray(record['cooldowns']) ? record['cooldowns'] : [];
    record['cooldowns'] = cooldowns.map(withCooldownSeconds);
    record['schemaVersion'] = 2;
  }

  // A record that reaches here without the field - hand-edited, or written by a
  // build between these versions - still needs one, or time could not pass.
  if (typeof record['elapsedSeconds'] !== 'number') record['elapsedSeconds'] = 0;

  record = migrateNestedEncounter(record);
  return record;
}

/**
 * Canonicalizes the encounter nested in a runtime record.
 *
 * THREE jobs, and THE ORDER IS THE WHOLE POINT:
 *
 * 1. MIGRATE the nested encounter to version 4, 2. RESCUE its Effects and
 *    cooldowns, 3. apply the 4 -> 5 ownership transfer.
 *
 * The first version of this did 2 before 1, and was only correct for a version
 * 4 encounter. Migrating first is what makes the rescue honest for every
 * version the chain knows:
 *
 *  - v1 timed Effects hold only an Effect ID and cannot be reconstructed. The
 *    established 1 -> 2 policy DROPS them. Rescuing them raw would have
 *    contradicted that policy and put ID-only stubs into `runtime.active`.
 *  - v2 timed Effects hold `roundsRemaining`; 2 -> 3 turns it into `endsWhen`.
 *  - v3 elapsed-time conditions hold `minutes`; 3 -> 4 turns them into
 *    `seconds`.
 *
 * So nothing reaches `runtime.active` that is not already a current-form
 * `TimedEffectRecord`, and nothing reaches `runtime.cooldowns` that is not a
 * current-form `CooldownRecord`.
 *
 * Rescued records are APPENDED, and anything the runtime already holds wins.
 * A legacy nested encounter and a populated outer runtime cannot both be
 * authoritative about the same Effect, and the outer one is the newer claim.
 *
 * On reachability: no released build ever wrote a pre-5 encounter INSIDE a
 * runtime record. Encounters below 5 had their own file and their own
 * repository, neither of which the current `StoragePort` can address - there is
 * no `readEncounter`, and the Electron file store has no `encounters` directory.
 * So this path exists for hand-edited files and for anyone reconstructing an old
 * encounter by hand, not because the application can produce one. It is written
 * as a rescue anyway, because the alternative is a migration that silently
 * discards real data if the assumption is ever wrong.
 */
function migrateNestedEncounter(record: Record<string, unknown>): Record<string, unknown> {
  const nested = record['encounter'];
  if (typeof nested !== 'object' || nested === null || Array.isArray(nested)) return record;

  // STEP 1. Everything below reads the MIGRATED payload, never the raw one.
  const migrated = migrateEncounterThroughV4(nested);
  if (typeof migrated !== 'object' || migrated === null) return record;
  const encounter = migrated as Record<string, unknown>;
  const next = { ...record };

  // STEP 2. These are now in current form, because step 1 put them there.
  const strandedEffects = Array.isArray(encounter['timedEffects']) ? encounter['timedEffects'] : [];
  const strandedCooldowns = (
    Array.isArray(encounter['cooldowns']) ? encounter['cooldowns'] : []
  ).map(withCooldownSeconds);

  if (strandedEffects.length > 0) {
    const existing = Array.isArray(next['active']) ? next['active'] : [];
    const held = new Set(existing.map((e) => effectIdOf(e)).filter((id) => id !== null));
    next['active'] = [
      ...existing,
      ...strandedEffects.filter((e) => {
        const id = effectIdOf(e);
        return id === null || !held.has(id);
      }),
    ];
  }

  if (strandedCooldowns.length > 0) {
    const existing = Array.isArray(next['cooldowns']) ? next['cooldowns'] : [];
    const held = new Set(existing.map((c) => sourceIdOf(c)).filter((id) => id !== null));
    next['cooldowns'] = [
      ...existing,
      ...strandedCooldowns.filter((c) => {
        const id = sourceIdOf(c);
        return id === null || !held.has(id);
      }),
    ];
  }

  // STEP 3. Only now does the encounter give the fields up, and only then does
  // it gain the phase (5 -> 6). Both steps run here rather than one of them
  // being skipped when the record arrives already at 5.
  const transferred = transferEncounterOwnedData(encounter);
  next['encounter'] =
    typeof transferred === 'object' && transferred !== null
      ? applyEvadeResultDefault(
          applyIncomingAttacksDefault(
            applyEncounterPhaseDefault(transferred as Record<string, unknown>),
          ),
        )
      : transferred;
  return next;
}

/**
 * Gives a cooldown the `secondsRemaining` the current shape carries.
 *
 * SHARED with the runtime 1 -> 2 step rather than written twice, because a
 * cooldown rescued out of a legacy encounter needs exactly what a cooldown
 * migrated from a legacy runtime needs. A record that already has the field has
 * been canonicalized by something and is left alone; anything that predates the
 * field gets its FULL duration back, which is the same conservative choice
 * documented on the 1 -> 2 step. Waiting slightly too long is recoverable at
 * the table; an ability that quietly recharges itself is not.
 */
function withCooldownSeconds(entry: unknown): unknown {
  if (typeof entry !== 'object' || entry === null) return entry;
  const c = entry as Record<string, unknown>;
  if (typeof c['secondsRemaining'] === 'number') return c;
  const seconds = cooldownDurationSeconds(c as unknown as CooldownRecord);
  return seconds === null ? c : { ...c, secondsRemaining: seconds };
}

function effectIdOf(entry: unknown): string | null {
  if (typeof entry !== 'object' || entry === null) return null;
  const effect = (entry as Record<string, unknown>)['effect'];
  if (typeof effect !== 'object' || effect === null) return null;
  const id = (effect as Record<string, unknown>)['id'];
  return typeof id === 'string' ? id : null;
}

function sourceIdOf(entry: unknown): string | null {
  if (typeof entry !== 'object' || entry === null) return null;
  const id = (entry as Record<string, unknown>)['sourceId'];
  return typeof id === 'string' ? id : null;
}

/** True when a fight is in progress. */
export function inEncounter(runtime: RuntimeState): boolean {
  return runtime.encounter !== null;
}
